import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { MdChevronLeft, MdChevronRight, MdAutoAwesome } from "react-icons/md";
import toast from "react-hot-toast";
import OptimizedImage from "../OptimizedImage/OptimizedImage";
import ZoomButton from "../buttons/ZoomButton";
import Toggle from "../Toggle/Toggle";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import styles from "./ComparisonViewer.module.css";

export default function ComparisonViewer({
  mode,
  originalSrc,
  originalAlt,
  transformedSrc,
  transformedAlt,
  slides = [],
  shouldPreload,
  onOpenZoomOriginal,
  onOpenZoomTransformed,
  sideCarouselKey,
  sideCarouselState,
  scrollSideCarousel,
  updateSideCarouselState,
  onActiveIndexChange,
}) {
  const userRole = useSelector((state) => state.auth?.role);
  const isAdmin = userRole === "admin";
  const isUserRole = userRole === "user" || userRole !== "admin";

  const dragBoxRef = useRef(null);

  // Normalized list of AI generated images for this product
  const aiList = useMemo(() => {
    let list = [];
    if (Array.isArray(slides) && slides.length > 0) {
      list = slides;
    } else if (transformedSrc) {
      list = [
        {
          transformed: transformedSrc,
          preview: transformedSrc,
          transformedAlt: transformedAlt,
        },
      ];
    }
    if (isUserRole) {
      list = list.filter((s) => {
        if (s?.bIsActive !== undefined) return Boolean(s.bIsActive && s.bIsActive !== "0" && s.bIsActive !== 0);
        if (s?.isActive !== undefined) return Boolean(s.isActive && s.isActive !== "0" && s.isActive !== 0);
        if (s?.status !== undefined) return Boolean(s.status === true || s.status === 1 || s.status === "1" || s.status === "active");
        return true;
      });
    }
    return list;
  }, [slides, transformedSrc, transformedAlt, isUserRole]);

  // Internal carousel state for cycling through AI generated images
  const [internalIndex, setInternalIndex] = useState(0);
  const [mobileTab, setMobileTab] = useState("original");

  // Reset tab to original and carousel index when product changes
  useEffect(() => {
    setMobileTab("original");
    setInternalIndex(0);
  }, [originalSrc]);

  const safeIndex =
    aiList.length > 0
      ? ((internalIndex % aiList.length) + aiList.length) % aiList.length
      : 0;

  useEffect(() => {
    if (typeof onActiveIndexChange === "function") {
      onActiveIndexChange(safeIndex, aiList.length);
    }
  }, [safeIndex, aiList.length, onActiveIndexChange]);

  const currentItem = aiList[safeIndex] || {};
  const currentTransformedSrc =
    currentItem.preview || currentItem.transformed || transformedSrc;
  const currentTransformedAlt =
    currentItem.transformedAlt ||
    currentItem.previewAlt ||
    transformedAlt ||
    "AI Generated Look";
  const currentSlideId =
    currentItem.id ??
    currentItem.iSlideId ??
    currentItem.slideId ??
    currentItem.iShowTellId;
  const currentSlideActive =
    currentItem.bIsActive !== undefined
      ? Boolean(currentItem.bIsActive)
      : currentItem.isActive !== undefined
        ? Boolean(currentItem.isActive)
        : currentItem.status !== undefined
          ? Boolean(currentItem.status === true || currentItem.status === 1 || currentItem.status === "active")
          : true;

  const [toggleLoading, setToggleLoading] = useState(false);
  const token = useSelector((state) => state.auth?.token);

  const handleToggleSlide = async () => {
    if (!currentSlideId || toggleLoading) return;
    const previousState = currentSlideActive;
    const newState = !previousState;
    currentItem.bIsActive = newState;
    currentItem.isActive = newState;
    setToggleLoading(true);

    try {
      const response = await apiHandler({
        url: `${endpoint.TOGGLE_SHOW_TELL_SLIDE}/${currentSlideId}/toggle`,
        method: "PATCH",
        authToken: { token },
        noCache: true,
      });

      if (
        (response?.status && response.status >= 400) ||
        response?.data?.status === "error" ||
        response?.data?.success === false
      ) {
        throw new Error(response?.data?.message || "Failed to update slide status");
      }

      toast.success(
        response?.data?.message || (newState ? "Slide enabled" : "Slide disabled"),
      );
    } catch (err) {
      currentItem.bIsActive = previousState;
      currentItem.isActive = previousState;
      toast.error(err?.message || "Failed to update slide status");
    } finally {
      setToggleLoading(false);
    }
  };

  const hasMultipleAiImages = aiList.length > 1;

  const handlePrev = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!hasMultipleAiImages) return;
    setInternalIndex((prev) => (prev - 1 + aiList.length) % aiList.length);
  };

  const handleNext = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!hasMultipleAiImages) return;
    setInternalIndex((prev) => (prev + 1) % aiList.length);
  };

  // Touch swipe support for cycling through AI generation gallery
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);

  const handleTouchStart = (e) => {
    if (!hasMultipleAiImages) return;
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (!hasMultipleAiImages || touchStartXRef.current === null) return;
    const touch = e.changedTouches[0];
    const diffX = touch.clientX - touchStartXRef.current;
    const diffY =
      touchStartYRef.current !== null
        ? Math.abs(touch.clientY - touchStartYRef.current)
        : 0;

    // Only switch looks if swipe is clearly horizontal
    if (Math.abs(diffX) > 35 && Math.abs(diffX) > diffY) {
      if (diffX > 0) {
        // Swiped right -> previous look
        handlePrev(e);
      } else {
        // Swiped left -> next look
        handleNext(e);
      }
    }
    touchStartXRef.current = null;
    touchStartYRef.current = null;
  };

  return (
    <div
      className={`${mode === "side" ? "sideBySideActive" : "compareActive"} w-full h-full flex items-center justify-center relative`}
      data-side-carousel-shell
    >
      <div
        ref={dragBoxRef}
        className={`${styles.originalTransformBox} originalTransformBoxClass`}
        data-drag-box
        onScroll={(event) =>
          mode === "side" &&
          updateSideCarouselState(
            sideCarouselKey,
            event.currentTarget,
          )
        }
      >
        {/* Mobile Segmented Pill: Original vs AI Generated (placed inside card frame) */}
        {mode === "side" && (
          <div className="sm:hidden absolute top-2.5 left-1/2 -translate-x-1/2 z-30 flex items-center bg-white/95 backdrop-blur-md p-0.5 rounded-full border border-slate-200/90 shadow-[0_2px_12px_rgba(15,23,42,0.12)]">
            <button
              type="button"
              onClick={() => setMobileTab("original")}
              className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all cursor-pointer border-none ${
                mobileTab === "original"
                  ? "bg-primary text-white shadow-xs"
                  : "text-slate-600 hover:text-slate-900 bg-transparent"
              }`}
            >
              Original
            </button>
            <button
              type="button"
              onClick={() => setMobileTab("ai")}
              className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all cursor-pointer border-none flex items-center gap-1 ${
                mobileTab === "ai"
                  ? "bg-primary text-white shadow-xs"
                  : "text-slate-600 hover:text-slate-900 bg-transparent"
              }`}
            >
              <MdAutoAwesome className="text-[12px]" />
              <span>AI</span>
            </button>
          </div>
        )}

        {/* Original Product display element (Left side) */}
        <div
          className={`${styles.original} originalClass border border-outline bg-surface-container rounded-xl shadow-sm overflow-hidden ${
            mode === "side" && mobileTab !== "original" ? "mobileHiddenCard" : ""
          }`}
        >
          <div className={`${styles.productImageBox} w-full h-full flex items-center justify-center bg-transparent`}>
            <OptimizedImage
              src={originalSrc}
              alt={originalAlt}
              priority={shouldPreload}
              rootMargin="50px"
              className="w-full h-full object-contain bg-transparent"
            />
          </div>
          <ZoomButton
            modalTarget="#modelZoomModal"
            onClick={onOpenZoomOriginal}
            positionClasses="absolute bottom-4 left-4 lg:bottom-2.5 lg:left-2.5"
            size="lg"
            className="showTellZoomButton z-20"
          />
        </div>

        {/* Transformed AI display element (Right side with internal carousel) */}
        <div
          className={`${styles.transformed} transformedClass border border-outline bg-surface-container rounded-xl shadow-sm overflow-hidden relative group/aiCarousel swiper-no-swiping ${
            mode === "side" && mobileTab !== "ai" ? "mobileHiddenCard" : ""
          }`}
          data-transformed
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className={`${styles.productImageBox} w-full h-full flex items-center justify-center bg-transparent`}>
            {currentTransformedSrc ? (
              <OptimizedImage
                src={currentTransformedSrc}
                alt={currentTransformedAlt}
                priority={shouldPreload}
                rootMargin="50px"
                className="w-full h-full object-contain bg-transparent"
              />
            ) : (
              <div className="flex flex-col items-center justify-center p-6 text-center text-slate-400">
                <MdAutoAwesome className="text-3xl mb-2 text-slate-300 animate-pulse" />
                <p className="text-xs font-semibold">No generations yet</p>
              </div>
            )}
          </div>

          {/* Internal Carousel Chevrons (desktop/tablet only in side-by-side mode) */}
          {mode === "side" && hasMultipleAiImages && (
            <>
              <button
                type="button"
                aria-label="Previous generated look"
                className="app-icon-button swiper-no-swiping hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full items-center justify-center z-20 shadow-[0_2px_8px_rgba(0,0,0,0.2)] bg-white/95 hover:bg-white text-slate-800 focus:outline-none transition-all hover:scale-105 active:scale-95 cursor-pointer opacity-90 group-hover/aiCarousel:opacity-100"
                onClick={handlePrev}
              >
                <MdChevronLeft size={20} />
              </button>
              <button
                type="button"
                aria-label="Next generated look"
                className="app-icon-button swiper-no-swiping hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center z-20 shadow-[0_2px_8px_rgba(0,0,0,0.2)] bg-white/95 hover:bg-white text-slate-800 focus:outline-none transition-all hover:scale-105 active:scale-95 cursor-pointer opacity-90 group-hover/aiCarousel:opacity-100"
                onClick={handleNext}
              >
                <MdChevronRight size={20} />
              </button>
            </>
          )}

          {/* Internal Carousel Counter & Controls (in side-by-side mode) */}
          {mode === "side" && hasMultipleAiImages && (
            <div className="absolute bottom-2.5 left-0 right-0 flex justify-center items-center z-20 pointer-events-auto">
              <div className="flex items-center gap-0.5 bg-white/90 backdrop-blur-md border border-white/80 shadow-[0_2px_10px_rgba(15,23,42,0.1)] rounded-full px-1.5 py-0.5 text-slate-700 text-[10px] font-medium tracking-wide select-none swiper-no-swiping">
                <button
                  type="button"
                  aria-label="Previous look"
                  className="w-5 h-5 rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 active:scale-95 transition-all cursor-pointer border-none bg-transparent"
                  onClick={handlePrev}
                >
                  <MdChevronLeft size={16} />
                </button>
                <span className="px-1 text-slate-700 font-semibold tabular-nums">
                  <span className="font-bold text-slate-900">{safeIndex + 1}</span>
                  <span className="text-slate-400 mx-0.5">/</span>
                  <span>{aiList.length}</span>
                </span>
                <button
                  type="button"
                  aria-label="Next look"
                  className="w-5 h-5 rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 active:scale-95 transition-all cursor-pointer border-none bg-transparent"
                  onClick={handleNext}
                >
                  <MdChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Zoom button on bottom right */}
          <ZoomButton
            modalTarget="#modelZoomModal"
            onClick={() => onOpenZoomTransformed(currentTransformedSrc)}
            positionClasses="absolute bottom-4 right-4 lg:bottom-2.5 lg:right-2.5"
            size="lg"
            className="showTellZoomButton z-20 swiper-no-swiping"
          />

          {/* Controls / Badges in Transformed Element */}
          {mode === "compare" ? (
            <div className="absolute top-2.5 right-2.5 z-30 flex flex-col items-end gap-1.5">
              {isAdmin && currentSlideId && (
                <Toggle
                  checked={currentSlideActive}
                  onChange={handleToggleSlide}
                  disabled={toggleLoading}
                />
              )}
              <div className="flex bg-surface-container-lowest/90 backdrop-blur rounded px-1.5 py-0.5 border border-outline items-center gap-1 shadow-sm pointer-events-none">
                <MdAutoAwesome className="text-[12px] text-primary" />
                <span className="text-[10px] text-primary font-bold">AI</span>
              </div>
            </div>
          ) : (
            <>
              {/* Slide Active Toggle (Top-Right) - Only for Admin */}
              {isAdmin && currentSlideId && (
                <div className="absolute top-2.5 right-2.5 z-30">
                  <Toggle
                    checked={currentSlideActive}
                    onChange={handleToggleSlide}
                    disabled={toggleLoading}
                  />
                </div>
              )}

              {/* AI label badge (Top-Left in side-by-side mode) */}
              <div className="flex absolute top-2.5 left-2.5 bg-surface-container-lowest/90 backdrop-blur rounded px-1.5 py-0.5 border border-outline items-center gap-1 shadow-sm z-20 pointer-events-none">
                <MdAutoAwesome className="text-[12px] text-primary" />
                <span className="text-[10px] text-primary font-bold">AI</span>
              </div>
            </>
          )}
        </div>

        {/* Draggable vertical compare slider handle */}
        <div
          className={`${styles.dragHandle} dragHandleClass`}
          data-drag-handle
        ></div>
      </div>

      {/* Compare mode floating generation switcher */}
      {mode === "compare" && hasMultipleAiImages && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-white/90 backdrop-blur-md border border-white/80 shadow-[0_2px_10px_rgba(15,23,42,0.08)] rounded-full px-1 py-0.5 z-30 pointer-events-auto swiper-no-swiping transition-all duration-200 hover:bg-white hover:shadow-[0_4px_14px_rgba(15,23,42,0.12)]">
          <button
            type="button"
            aria-label="Previous look"
            className="w-5 h-5 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 active:scale-95 transition-all cursor-pointer focus:outline-none"
            onClick={handlePrev}
            title="Previous look"
          >
            <MdChevronLeft size={15} />
          </button>

          <span className="text-[10px] font-medium text-slate-500 select-none px-0.5 tracking-tight tabular-nums leading-none">
            <span className="font-bold text-slate-900">{safeIndex + 1}</span>
            <span className="text-slate-300 mx-0.5 font-normal">/</span>
            <span>{aiList.length}</span>
          </span>

          <button
            type="button"
            aria-label="Next look"
            className="w-5 h-5 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 active:scale-95 transition-all cursor-pointer focus:outline-none"
            onClick={handleNext}
            title="Next look"
          >
            <MdChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
